import React, { useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  Label,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import { ReactComponent as IconInfo } from "../../../../shared/assets/Icons/circle-info-solid.svg";
import { ChartContainer } from "./ChartContainer";
import { PRESETS_PHYSICAL, formatDuration } from "./timeControls";
import { normalizePortBandwidthForDisplay } from "./bandwidthDisplay";

export interface BandwidthPoint {
  time: string;
  value: number;
}

export interface LinkBandwidth {
  inbound: BandwidthPoint[];
  outbound: BandwidthPoint[];
}

/**
 * Reads one link's series for a preset range. Null (or a throw) renders the
 * "unavailable" state. Keep its identity stable (`useCallback`): a new
 * function refetches.
 */
export type FetchLinkBandwidth = (
  interval: string,
  timeRange: string
) => Promise<LinkBandwidth | null>;

interface ChartPoint {
  time: string;
  inbound?: number;
  outbound?: number;
  formattedTime: string;
}

interface Props {
  /** Customer-facing link label, e.g. "Uplink — QRO1 (AXTEL)". */
  title: string;
  fetchBandwidth: FetchLinkBandwidth;
}

/** Same time formatting the universal chart uses. */
const toChartData = (
  inbound: BandwidthPoint[],
  outbound: BandwidthPoint[]
): ChartPoint[] => {
  const length = Math.max(inbound.length, outbound.length);
  return Array.from({ length }, (_, i) => {
    const ts = inbound[i]?.time ?? outbound[i]?.time ?? "";
    const d = ts ? new Date(ts) : new Date();
    return {
      time: ts,
      // Keep absent samples undefined so Recharts skips them (no 0 spike).
      inbound: inbound[i]?.value ?? undefined,
      outbound: outbound[i]?.value ?? undefined,
      formattedTime: d.toLocaleString("en-US", {
        timeZone: "America/Mexico_City",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }),
    };
  });
};

/**
 * One Bandwidth Utilization card per link, so a detail view can stack
 * several — the Extended PoP's uplinks, the carriers of a Carrier Access.
 * Same visual language as `BandwidthChartUniversal`, but self-contained
 * (local state, a fetch passed in): the universal chart is bound to a single
 * Redux slice and several instances of it would overwrite each other.
 *
 * Preset time ranges only; the physical-port presets apply because the
 * links are router interfaces.
 */
const LinkBandwidthChart: React.FC<Props> = ({ title, fetchBandwidth }) => {
  const [timeRange, setTimeRange] = useState<string>("5m");
  const [interval, setIntervalValue] = useState<string>("5s");
  const [inbound, setInbound] = useState<BandwidthPoint[]>([]);
  const [outbound, setOutbound] = useState<BandwidthPoint[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [failed, setFailed] = useState<boolean>(false);
  const [showInbound, setShowInbound] = useState<boolean>(true);
  const [showOutbound, setShowOutbound] = useState<boolean>(true);

  const timeRangeOptions = useMemo(
    () =>
      PRESETS_PHYSICAL.map((preset) => ({
        value: preset.timeRange as string,
        label: formatDuration(preset.timeRange),
      })),
    []
  );

  const intervalOptions = useMemo(() => {
    const preset = PRESETS_PHYSICAL.find((p) => p.timeRange === timeRange);
    const buckets = preset
      ? Object.values(preset.changeIntervals)
      : (["5s"] as string[]);
    return [...new Set(buckets)].map((value) => ({
      value: value as string,
      label: formatDuration(value),
    }));
  }, [timeRange]);

  const handleTimeRangeSelect = (label: string) => {
    const option = timeRangeOptions.find((o) => o.label === label);
    if (!option) return;
    setTimeRange(option.value);
    // Every range carries its own valid intervals; snap to the finest one.
    const preset = PRESETS_PHYSICAL.find((p) => p.timeRange === option.value);
    if (preset) setIntervalValue(Object.values(preset.changeIntervals)[0]);
  };

  const handleIntervalSelect = (label: string) => {
    const option = intervalOptions.find((o) => o.label === label);
    if (option) setIntervalValue(option.value);
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setFailed(false);
        const series = await fetchBandwidth(interval, timeRange);
        if (cancelled) return;
        if (series) {
          setInbound(series.inbound);
          setOutbound(series.outbound);
        } else {
          setFailed(true);
        }
      } catch {
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [fetchBandwidth, timeRange, interval]);

  const normalized = useMemo(
    () =>
      normalizePortBandwidthForDisplay({
        inbound,
        outbound,
        unit: "Mbps",
      }),
    [inbound, outbound]
  );

  const chartData = useMemo(
    () => toChartData(normalized.inbound, normalized.outbound),
    [normalized]
  );

  const hasData = chartData.length > 0;

  return (
    <GenericCard
      isCollapsible={false}
      headerContent={
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 w-full">
          <span>{`Bandwidth Utilization — ${title}`}</span>
          <div className="flex items-end gap-2">
            <div className="flex flex-col">
              <p className="text-sm font-normal">Time Range</p>
              <GenericDropdown
                items={timeRangeOptions.map((o) => o.label)}
                value={
                  timeRangeOptions.find((o) => o.value === timeRange)?.label ??
                  "Select Time Range"
                }
                onSelect={handleTimeRangeSelect}
                defaultLabel="Select Time Range"
                size="sm"
              />
            </div>
            <div className="flex flex-col">
              <p className="text-sm font-normal">Change Interval</p>
              <GenericDropdown
                items={intervalOptions.map((o) => o.label)}
                value={
                  intervalOptions.find((o) => o.value === interval)?.label ??
                  "Select Interval"
                }
                onSelect={handleIntervalSelect}
                defaultLabel="Select Interval"
                size="sm"
              />
            </div>
          </div>
        </div>
      }
      bodyContent={
        <>
          <div className="relative h-[300px]">
            {!loading && (failed || !hasData) && (
              <div className="absolute inset-0 flex flex-col items-center justify-center z-10">
                <IconInfo className="w-8 h-8 text-yellow-500" />
                <p className="mt-2 text-sm text-semantic-text-secondary">
                  Performance data is unavailable at the moment.
                </p>
              </div>
            )}
            <div className="relative w-90% mx-5">
              <ChartContainer
                config={{
                  inbound: {
                    label: "Inbound",
                    color: "var(--color-primitive-brand-surf-300)",
                  },
                  outbound: {
                    label: "Outbound",
                    color: "var(--color-primitive-brand-surf-700)",
                  },
                }}
                className="h-[300px]"
              >
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={chartData}
                    margin={{ top: 30, right: 50, left: 40, bottom: 40 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="formattedTime"
                      tickLine={false}
                      axisLine={false}
                      tickMargin={10}
                      tick={{ fontSize: 12, fill: "#666" }}
                      interval="preserveStartEnd"
                    >
                      <Label
                        value="Time (Mexico City)"
                        position="bottom"
                        offset={20}
                        style={{ textAnchor: "middle", fontSize: 12 }}
                      />
                    </XAxis>
                    <YAxis
                      tickLine={false}
                      axisLine={false}
                      tickMargin={10}
                      tick={{ fontSize: 12, fill: "#666" }}
                    >
                      <Label
                        value="Mean Bandwidth Usage"
                        angle={-90}
                        position="insideLeft"
                        offset={-23}
                        style={{ textAnchor: "middle", fontSize: 12 }}
                      />
                      <Label
                        value={`(${normalized.unit})`}
                        angle={-90}
                        position="insideLeft"
                        offset={-5}
                        style={{ textAnchor: "middle", fontSize: 12 }}
                      />
                    </YAxis>
                    <Tooltip
                      formatter={(value: number, name: string) => [
                        `${value} ${normalized.unit}`,
                        name,
                      ]}
                    />
                    {showInbound && (
                      <Line
                        type="monotone"
                        dataKey="inbound"
                        name="Inbound"
                        stroke="var(--color-inbound)"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        activeDot={{ r: 6 }}
                      />
                    )}
                    {showOutbound && (
                      <Line
                        type="monotone"
                        dataKey="outbound"
                        name="Outbound"
                        stroke="var(--color-outbound)"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        activeDot={{ r: 6 }}
                      />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </ChartContainer>
              {loading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 z-10">
                  <span className="text-lg font-semibold text-gray-700">
                    Loading...
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="flex justify-end my-5 mr-10">
            <div className="flex flex-wrap items-center gap-4">
              <button
                onClick={() => setShowInbound((v) => !v)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-all border ${
                  showInbound
                    ? "border-[var(--color-semantic-border-brand-secondary)] bg-[var(--color-semantic-bg-primary)] text-[var(--color-semantic-text-primary)]"
                    : "border-semantic-border-secondary text-semantic-text-secondary opacity-60"
                }`}
              >
                <span
                  className="w-3 h-3 rounded-full"
                  style={{
                    background: "var(--color-primitive-brand-surf-300)",
                  }}
                />
                <span className="text-sm">Inbound</span>
              </button>
              <button
                onClick={() => setShowOutbound((v) => !v)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-all border ${
                  showOutbound
                    ? "border-[var(--color-semantic-border-brand-secondary)] bg-[var(--color-semantic-bg-primary)] text-[var(--color-semantic-text-primary)]"
                    : "border-semantic-border-secondary text-semantic-text-secondary opacity-60"
                }`}
              >
                <span
                  className="w-3 h-3 rounded-full"
                  style={{
                    background: "var(--color-primitive-brand-surf-700)",
                  }}
                />
                <span className="text-sm">Outbound</span>
              </button>
            </div>
          </div>
        </>
      }
    />
  );
};

export default LinkBandwidthChart;
